export default {
  id: "payroll-mileage",
  title: "Payroll & Mileage",
  section: "Billing & Reports",
  order: 60,
  keywords: "payroll pay period csv export mileage odometer deduction transport reimbursement cap batch",
  body: `
## What it is

The Payroll page turns a pay period into the numbers your payroll provider
needs: hours per caregiver plus payable mileage, exported as CSV. Mileage
is captured automatically at clock-in/clock-out via odometer readings, so
reimbursement comes out of the same data as everything else — no separate
mileage sheets.

## How payroll works

- Pick the pay period; the page builds a per-caregiver summary of hours from
  completed shifts.
- **Side hours count**: shadow- and training-role caregivers are paid for
  their clocked time even though they don't appear on billing — including
  side hours on shifts whose main status ended up Missed.
- **Payable miles** are included per caregiver (see below).
- Exported batches are **saved**: a past period's CSV can be re-downloaded
  later exactly as it was originally sent, even if data changed afterward.

### Step by step: running payroll

1. Get the period's shifts approved (or at least clocked out — check the
   Shifts page for stragglers and Error shifts needing time fixes).
2. Open **Payroll**, select the pay period.
3. Review the per-caregiver summary — hours and miles. Chase anything
   surprising back to the underlying shifts (click through or use the
   Shifts page filters).
4. Download the CSV and hand it to payroll. The batch is stored for later
   reference.

## How mileage works

**Capture** happens at the clock:

- At **clock-in** the caregiver can record a starting odometer reading and
  location. The fields are always available — whether or not they're
  transporting a client.
- If a starting reading exists, **clock-out requires** the ending odometer,
  places visited, and ending location. A trip can't be left open.
- The **"transporting client" checkbox does not unlock anything** — it only
  marks the trip as client transport, which matters for the deduction below.

**Payable miles** are computed fresh from the readings every time a report
or payroll runs (nothing is stored), per caregiver per day:

> payable = transport miles + (non-transport miles − daily deduction, if
> positive)

The daily deduction (default **30 miles**) is the commute allowance: the
first 30 non-transport miles each day are on the caregiver; everything
beyond, and *all* client-transport miles, is reimbursed. Because it's
computed at read time, correcting an odometer typo on a shift instantly
corrects payroll and every report.

## Approving miles

Miles are approved **together with the shift**. When you approve a shift
that has a recorded trip, the Approve dialog shows the trip with an
**"Approve mileage" checkbox already checked** and the recorded miles in
an editable number field:

- Leave it checked and untouched → the miles are approved as recorded.
- **Correct the number** → the adjusted miles are what gets reimbursed
  (the dialog notes "adjusted from X mi", and the shift history records
  both numbers).
- **Uncheck it** → the shift is approved but the miles are **held**: they
  won't appear on payroll or reports. To pay them later, undo the shift
  approval and re-approve with mileage checked.
- Group shifts show one row per caregiver trip, each approved separately.
- Undoing a shift approval also un-approves its miles.

> **Only approved miles are reimbursed.** Payroll, the caregiver's
> dashboard stat, and report dollar amounts all count approved miles only.
> Miles recorded before this feature existed (on already-approved shifts)
> were grandfathered in as approved.

## Miles vs. dollars — what shows where

The **submitted payroll CSV shows dollars, not miles**: a "Mileage ($)"
column equal to approved payable miles × the per-mile rate from Settings →
Mileage. Admin screens keep showing the miles themselves:

- **Payroll page** — approved payable miles per caregiver, the dollar
  amount next to them, and a **"pending" warning** when recorded miles are
  still awaiting approval (approve those shifts' mileage before exporting,
  or the money is left behind).
- **Caregiver dashboard** — approved payable miles for the period.
- **Calendar** — per-shift recorded miles in tooltips, the shift details
  popup, weekly totals, and the caregiver's total-miles chip.
- **Mileage Report** — every trip (transport or not) with its recorded
  miles, an **Approved** column, the approved miles, and the per-trip
  **Reimbursement ($)** — the audit view of the whole system.

### Step by step: fixing a bad odometer entry

1. Find the shift (Shifts page or calendar) and open its edit.
2. Correct the odometer start/end. Admin edits require a change reason.
3. Done — payroll, dashboard, and reports all recalculate automatically.

## The mileage allowance

Each caregiver has a mileage allowance: a **dollar cap per pay period**
(periods run the 1st–15th and 16th–end of month; the cap, default $50, is
set in Settings → Mileage). It's a soft limit — the app **warns but never
blocks** — so overruns are a conversation, not a wall. Where you see it:

- **In the Approve Shift dialog** — under the mileage toggle, a meter shows
  the caregiver's period so far: "$35.00 used + $12.00 pending of $50.00 ·
  this approval adds ≈ $15.40". It turns amber past the alert threshold and
  red with "exceeds the allowance by $X" when the approval would go over —
  you can still approve. ("Used" = approved miles; "pending" = recorded
  miles still awaiting approval on other shifts.)
- **On the Payroll page** — an Allowance column per caregiver
  ("$35.00 of $50.00 (70%)") so the whole team is scannable before export.
- **On the caregiver's own dashboard** — their usage and what's left this
  period, so they can self-manage before anyone gets alerted.
- **Overnight manager alerts** still fire when a caregiver crosses the
  threshold (default 80%), landing in the Dashboard sidebar count.

The approval meter's "adds ≈" figure is approximate for non-transport
trips, because the daily 30-mile deduction depends on the whole day's
driving — the payroll number is always the exact one.

## Mileage settings

**Settings → Mileage** controls the policy: daily deduction miles, the
reimbursement rate per mile (what turns miles into dollars everywhere), the
per-period allowance cap, and the manager alert threshold.
`,
};
